/**
 * PublicProfile — a cinematic, Netflix-meets-Instagram-story showcase of an
 * author's shelf. Profile identity (avatar + name + stats) sits at the TOP over
 * a slowly drifting Ken-Burns banner built from the featured book's cover; below
 * it, a hero billboard for the featured book and horizontal rows of covers you
 * can browse. Selecting a cover crossfades the whole stage to that book.
 *
 * MVP: renders the CURRENT user's own shelf (a live preview of "your public
 * profile"), with per-book public/private curation persisted in localStorage.
 * Cross-user viewing via a shareable URL needs a backend endpoint (follow-up).
 */
import { useMemo, useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft, Share2, Eye, EyeOff, SlidersHorizontal, Check } from 'lucide-react';
import { GiRead, GiFountainPen } from '../components/icons/game-icons';
import { useAuthStore } from '../stores/auth-store';
import { useAppStore } from '../stores/app-store';
import { BookCover } from '../components/book-cover';
import type { Project } from '../types';

const WORDS_PER_PAGE = 275;
// guardamos os PRIVADOS (opt-out): tudo é público por padrão, mesmo livros
// carregados depois do mount. Assim a curadoria persiste sem snapshot.
const PRIVATE_KEY = 'reformu-private-books';

function wordsOf(p: Project): number {
  return p.chapters.reduce((s, c) => s + (c.wordCount ?? 0), 0);
}
function pagesOf(p: Project): number {
  return Math.max(1, Math.round(wordsOf(p) / WORDS_PER_PAGE));
}
function progressOf(p: Project): number {
  return Math.min(100, Math.round((wordsOf(p) / 80_000) * 100));
}

export function PublicProfile() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const projects = useAppStore((s) => s.projects);
  const fetchProjects = useAppStore((s) => s.fetchProjects);

  // /perfil é rota de topo (fora do AppLayout que busca os projetos), então
  // buscamos aqui no mount pra a estante aparecer mesmo em acesso direto.
  useEffect(() => { void fetchProjects(); }, [fetchProjects]);

  // Curation: which books are PRIVATE (hidden). Default = none (all public).
  const [privateIds, setPrivateIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(PRIVATE_KEY);
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch { /* ignore */ }
    return new Set();
  });
  const [manage, setManage] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(PRIVATE_KEY, JSON.stringify([...privateIds])); } catch { /* ignore */ }
  }, [privateIds]);

  const togglePublic = useCallback((id: string) => {
    setPrivateIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const isPublicId = useCallback((id: string) => !privateIds.has(id), [privateIds]);

  // ordered by most-recently updated
  const ordered = useMemo(
    () => [...projects].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
    [projects]
  );
  const publicBooks = useMemo(() => ordered.filter((p) => !privateIds.has(p.id)), [ordered, privateIds]);
  const shown = manage ? ordered : publicBooks;

  const [featuredId, setFeaturedId] = useState<string | null>(null);
  const featured = useMemo(
    () => shown.find((p) => p.id === featuredId) ?? shown[0] ?? null,
    [shown, featuredId]
  );

  const stats = useMemo(() => {
    const books = publicBooks.length;
    const words = publicBooks.reduce((s, p) => s + wordsOf(p), 0);
    const pages = publicBooks.reduce((s, p) => s + pagesOf(p), 0);
    return { books, words, pages };
  }, [publicBooks]);

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  const handle = '@' + (user.name || 'autor').toLowerCase().replace(/\s+/g, '');
  const bannerImg = featured?.metadata.coverImage;

  return (
    <div className="relative min-h-dvh overflow-x-hidden bg-[#080b14] text-white">
      {/* ── banner Ken-Burns (fundo cinematográfico que deriva devagar) ─── */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <AnimatePresence mode="popLayout">
          <motion.div
            key={featured?.id ?? 'none'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.1, ease: 'easeInOut' }}
            className="absolute inset-0"
          >
            {bannerImg ? (
              <motion.img
                src={bannerImg}
                alt=""
                className="h-full w-full object-cover"
                initial={{ scale: 1.12, x: '-2%', y: '-1%' }}
                animate={{ scale: 1.28, x: '2%', y: '2%' }}
                transition={{ duration: 22, ease: 'easeInOut', repeat: Infinity, repeatType: 'reverse' }}
              />
            ) : (
              <motion.div
                className="h-full w-full"
                style={{ background: 'radial-gradient(120% 90% at 30% 20%, #2a2350 0%, #12132a 45%, #080b14 100%)' }}
                initial={{ scale: 1.1 }}
                animate={{ scale: 1.25 }}
                transition={{ duration: 22, ease: 'easeInOut', repeat: Infinity, repeatType: 'reverse' }}
              />
            )}
            {/* véus pra legibilidade */}
            <div className="absolute inset-0 bg-[#080b14]/45 backdrop-blur-2xl" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#080b14]/70 via-[#080b14]/40 to-[#080b14]" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#080b14]/85 via-transparent to-[#080b14]/60" />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ── conteúdo ────────────────────────────────────────────────────── */}
      <div className="relative z-10">
        {/* top bar */}
        <div className="flex items-center justify-between px-5 py-4 md:px-10">
          <button
            onClick={() => navigate(-1)}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white/80 backdrop-blur transition-colors hover:bg-white/20"
            aria-label="Voltar"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setManage((m) => !m)}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider backdrop-blur transition-colors ${
                manage ? 'bg-[#F6E9C8] text-[#141005]' : 'bg-white/10 text-white/80 hover:bg-white/20'
              }`}
            >
              {manage ? <Check size={13} /> : <SlidersHorizontal size={13} />}
              {manage ? 'Concluir' : 'Curar estante'}
            </button>
            <button
              onClick={() => void navigator.clipboard?.writeText(`${location.origin}/perfil`)}
              className="flex h-9 items-center gap-1.5 rounded-full bg-white/10 px-3 text-[11px] font-semibold uppercase tracking-wider text-white/80 backdrop-blur transition-colors hover:bg-white/20"
            >
              <Share2 size={13} /> Compartilhar
            </button>
          </div>
        </div>

        {/* ── header do perfil (topo, estilo story) ─────────────────────── */}
        <motion.header
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center gap-4 px-6 pb-6 pt-4 text-center md:pt-8"
        >
          <div className="relative">
            <div className="absolute -inset-1.5 rounded-full bg-gradient-to-tr from-[#F6E9C8] via-[#c9a961] to-[#F6E9C8] opacity-80 blur-[2px]" />
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.name}
                className="relative h-24 w-24 rounded-full object-cover ring-2 ring-[#080b14] md:h-28 md:w-28"
              />
            ) : (
              <div className="relative grid h-24 w-24 place-items-center rounded-full bg-gradient-to-br from-[#2a2350] to-[#12132a] text-3xl font-serif ring-2 ring-[#080b14] md:h-28 md:w-28">
                {(user.name || 'A').charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div>
            <h1 className="font-serif text-3xl font-medium md:text-4xl">{user.name}</h1>
            <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-[#F6E9C8]/80">{handle}</p>
          </div>
          {/* stats */}
          <div className="flex items-center gap-6 font-mono text-xs uppercase tracking-wider text-white/70">
            <Stat n={stats.books} label="livros" />
            <span className="h-4 w-px bg-white/20" />
            <Stat n={stats.pages} label="páginas" />
            <span className="h-4 w-px bg-white/20" />
            <Stat n={stats.words} label="palavras" />
          </div>
          <p className="max-w-md text-sm text-white/55">
            A estante pública de {user.name}. Feito à mão, palavra por palavra. ✦
          </p>
        </motion.header>

        {shown.length === 0 ? (
          <div className="px-6 py-24 text-center text-white/50">
            Nenhum livro público ainda. {manage ? 'Toque no olho de um livro pra torná-lo público.' : 'Ative "Curar estante" pra escolher.'}
          </div>
        ) : (
          <>
            {/* ── HERO do livro em destaque (billboard) ─────────────────── */}
            {featured && (
              <AnimatePresence mode="wait">
                <motion.section
                  key={featured.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="mx-auto flex max-w-5xl flex-col items-center gap-7 px-6 py-8 md:flex-row md:items-center md:gap-12 md:py-12"
                >
                  {/* capa grande flutuando */}
                  <motion.div
                    initial={{ y: 0 }}
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 6, ease: 'easeInOut', repeat: Infinity }}
                    className="w-44 shrink-0 md:w-56"
                    style={{ filter: 'drop-shadow(0 30px 50px rgba(0,0,0,.6))' }}
                  >
                    <BookCover
                      title={featured.metadata.title || 'Sem título'}
                      author={featured.metadata.author || user.name}
                      genre={featured.metadata.genre || 'Fiction'}
                      coverImage={featured.metadata.coverImage}
                      variant="cover"
                      seed={featured.id}
                      progress={progressOf(featured)}
                    />
                  </motion.div>
                  {/* infos do livro */}
                  <div className="flex-1 text-center md:text-left">
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#F6E9C8]/80">
                      {featured.metadata.genre || 'Ficção'} · {pagesOf(featured)} páginas
                    </p>
                    <h2 className="mt-2 font-serif text-3xl font-medium leading-tight md:text-5xl">
                      {featured.metadata.title || 'Sem título'}
                    </h2>
                    {featured.metadata.description && (
                      <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/65 md:mx-0">
                        {featured.metadata.description}
                      </p>
                    )}
                    <div className="mt-6 flex items-center justify-center gap-3 md:justify-start">
                      <button
                        onClick={() => navigate(`/app/editor/${featured.id}`)}
                        className="flex items-center gap-2 rounded-full bg-[#F6E9C8] px-5 py-2.5 text-sm font-semibold text-[#141005] transition-transform hover:scale-105"
                      >
                        <GiRead size={18} /> Ler
                      </button>
                      <button
                        onClick={() => navigate(`/app/editor/${featured.id}`)}
                        className="flex items-center gap-2 rounded-full bg-white/10 px-5 py-2.5 text-sm font-semibold text-white/85 backdrop-blur transition-colors hover:bg-white/20"
                      >
                        <GiFountainPen size={18} style={{ transform: 'scaleX(-1)' }} /> Detalhes
                      </button>
                    </div>
                  </div>
                </motion.section>
              </AnimatePresence>
            )}

            {/* ── fileira de capas (Netflix) ────────────────────────────── */}
            <section className="px-5 pb-24 pt-4 md:px-10">
              <h3 className="mb-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white/50">
                {manage ? 'Todos os livros — escolha os públicos' : 'A estante'}
              </h3>
              <div className="flex gap-4 overflow-x-auto pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {shown.map((p, i) => {
                  const isPublic = isPublicId(p.id);
                  const isFeatured = featured?.id === p.id;
                  return (
                    <motion.button
                      key={p.id}
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: '-40px' }}
                      transition={{ delay: Math.min(i, 12) * 0.05, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                      whileHover={{ scale: 1.06, y: -6 }}
                      onClick={() => (manage ? togglePublic(p.id) : setFeaturedId(p.id))}
                      className={`group relative w-32 shrink-0 md:w-40 ${!isPublic && manage ? 'opacity-40' : ''}`}
                      style={{ filter: isFeatured ? 'drop-shadow(0 16px 30px rgba(246,233,200,.25))' : 'drop-shadow(0 12px 24px rgba(0,0,0,.5))' }}
                    >
                      <div className={`overflow-hidden rounded-lg ${isFeatured ? 'ring-2 ring-[#F6E9C8]' : ''}`}>
                        <BookCover
                          title={p.metadata.title || 'Sem título'}
                          author={p.metadata.author || user.name}
                          genre={p.metadata.genre || 'Fiction'}
                          coverImage={p.metadata.coverImage}
                          variant="cover"
                          seed={p.id}
                          progress={progressOf(p)}
                          disableTilt
                        />
                      </div>
                      {/* curadoria: olho público/privado */}
                      {manage && (
                        <span className="absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-full bg-black/60 backdrop-blur">
                          {isPublic ? <Eye size={14} className="text-[#F6E9C8]" /> : <EyeOff size={14} className="text-white/60" />}
                        </span>
                      )}
                      <p className="mt-2 line-clamp-1 text-center text-xs text-white/70 md:text-left">
                        {p.metadata.title || 'Sem título'}
                      </p>
                    </motion.button>
                  );
                })}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ n, label }: { n: number; label: string }) {
  return (
    <span className="flex flex-col items-center">
      <b className="text-lg font-bold text-white md:text-xl">{n.toLocaleString('pt-BR')}</b>
      <span className="text-[10px] text-white/50">{label}</span>
    </span>
  );
}
